/* =====================================================================
 * B2C tenant manifest (askyour.ai).
 *
 * This is the canonical source of truth for the public B2C product's
 * branding, identity, and asset references.  Values mirror the legacy
 * constants that previously lived scattered across `routeConfig.ts`,
 * `index.html`, and individual components — any change here surfaces
 * everywhere the app reads tenant metadata.
 *
 * Invariant
 *   `SITE_URL` / `APP_NAME` / `DEFAULT_OG_IMAGE` exposed by
 *   `@/routes/routeConfig` must continue to resolve to the exact
 *   strings they produced pre-Phase 3; the test in Phase 3d locks
 *   that parity.
 * ===================================================================== */

import {
  ADMIN_FEED_REFRESH,
  ADMIN_SKILLS_MANAGE,
  ADMIN_USERS_MANAGE,
  ADMIN_USERS_VIEW,
} from '@auw/permissions';
import { parseTenantManifest, type TenantManifest } from '@auw/tenant';

export const b2cManifest: TenantManifest = parseTenantManifest({
  version: 1,
  id: 'askyourai',

  identity: {
    appName: 'askyour.ai',
    siteUrl: 'https://askyour.ai',
    siteName: 'askyour.ai',
    locale: {
      default: 'ko',
      alternates: ['en', 'ja', 'zh-CN', 'zh-TW'],
    },
  },

  assets: {
    // Tiny agent mark also doubles as the browser favicon (keeps the
    // browser tab consistent with the in-app branding).
    favicon: '/agent_logo.png',
    agentLogo: '/agent_logo.png',
    agentLogoWebp: '/assets/agent_logo.webp',
    appNameLogo: '/app_name.png',
    // Dark-theme wordmark variant. The light wordmark is solid-black
    // text and is unreadable on a dark surface, so we ship a dedicated
    // light-on-dark file. `resolveAppNameLogo()` picks this up
    // automatically when the active theme is 'dark'; tenants that omit
    // this field render no wordmark in dark mode (resolver returns null).
    appNameLogoDark: '/app_name_dark.png',
    // Stored as a relative path so `absoluteAssetUrl()` can join it
    // onto the tenant's own `siteUrl`; this matches the legacy
    // `${SITE_URL}/assets/landing_ep01.webp` template exactly.
    defaultOgImage: '/assets/landing_ep01.webp',
    /**
     * Runtime font URLs (Phase 8).
     *
     * Mirrors the pre-Phase-8 `runtimeFontLoader.ts` constants so B2C
     * continues to pull Pretendard from jsdelivr and JetBrains Mono
     * from Google Fonts. On-prem tenants replace these with self-host
     * paths (or set them to empty strings to skip loading entirely).
     *
     * `externalRegistryEnabled: true` keeps the doc-agent font
     * registry surfacing Google Fonts / jsdelivr entries; on-prem
     * tenants flip this to `false` so docAgent shows system fonts
     * only.
     */
    fonts: {
      bodyHref:
        'https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/dist/web/variable/pretendardvariable-dynamic-subset.min.css',
      monospaceHref:
        'https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500&display=swap',
      externalRegistryEnabled: true,
    },
    /**
     * pdf.js worker source (Phase 8).
     *
     * Mirrors the pre-Phase-8 inline constant in `PdfRenderer.tsx`.
     * The URL is version-templated (`pdfjs-dist@${pdfjsLib.version}`)
     * at runtime so the worker always tracks the shipped library
     * version without a manifest rebuild. On-prem tenants replace
     * this with a self-hosted path (e.g. `/assets/pdf.worker.min.mjs`
     * served from the enterprise bundle).
     */
    pdfWorkerUrl: 'https://unpkg.com/pdfjs-dist@{version}/build/pdf.worker.min.mjs',
    /**
     * pdf.js CMap directory.
     *
     * Required so non-Latin PDFs (Korean / Japanese / Chinese — i.e.
     * the bulk of B2C uploads) render their glyphs correctly. pdfjs
     * appends the encoding-specific filename (e.g. `Adobe-Korea1-UCS2
     * .bcmap`) at runtime, so the value MUST end in a trailing `/`.
     *
     * Pairs with `cMapPacked: true` in the renderer because pdfjs-dist
     * ships the `.bcmap` (binary) variants here.
     */
    pdfCMapUrl: 'https://unpkg.com/pdfjs-dist@{version}/cmaps/',
    /**
     * pdf.js standard-font (Base14) directory.
     *
     * Required for PDFs that reference Helvetica / Times / Courier /
     * Symbol / Dingbats without embedding the glyphs. pdfjs-dist
     * ships Foxit-licensed `*.pfb` substitutes at this directory; we
     * point at the unpkg-hosted folder for B2C and self-host on-prem.
     * Trailing `/` is mandatory.
     */
    pdfStandardFontDataUrl: 'https://unpkg.com/pdfjs-dist@{version}/standard_fonts/',
  },

  /**
   * Feature flags — every id maps to a boolean that drives sidebar
   * visibility, route gating, and landing-page links.  Values mirror
   * B2C's pre-Phase-4 behaviour:
   *   - `mode.document` is off because the legacy
   *     `TEMP_HIDDEN_AGENT_PROFILE_IDS` / `TEMP_BLOCKED_MODE_SET`
   *     constants blocked it. Those constants were removed in
   *     Phase 4c; this manifest is now the single source of truth.
   *   - `mode.doc-agent` is off (dev/askyourai_dev): 문서 에이전트
   *     기능을 B2C 에서 숨긴다 (사이드바/모드전환/라우팅 모두 비노출).
   *     다시 켜려면 `true` 로 되돌린다.
   *   - Everything else stays on; public pages remain reachable for
   *     SEO / marketing and enterprise tenants can disable them by
   *     shipping their own manifest.
   */
  features: {
    /* Workspace-mode gates (Phase 4) — unchanged. */
    'mode.agent': true,
    'mode.live-agent': true,
    /* 문서 에이전트(doc-agent) 비활성화 — dev(askyourai_dev) 에서 끈다.
     * `false` 이면 (1) 사이드바 워크스페이스 목록에서 빠지고(Sidebar 가
     * `mode.${mode}` 로 필터), (2) 컴포저 모드 전환 목록에서 제외되며,
     * (3) `/doc-agent` 직접 진입 시 WorkspaceModeContent 가 실제 페이지
     * 대신 PlaceholderPage 로 게이트한다. 이미 off 인 `mode.document` 와
     * 동일한 패턴이라 백엔드 modules.order / AI manifest 의 doc_agent
     * 블록은 그대로 두고 프론트 플래그로만 노출을 차단한다. 다시 켜려면
     * `true`. 아래 `mode.doc-agent.*` 세부 플래그는 부모가 off 인 동안
     * 효력이 없다(되돌릴 때를 위해 값은 보존). */
    'mode.doc-agent': false,
    'mode.document': false,
    'mode.storage': true,
    'mode.message': true,
    'mode.widget': true,
    'mode.feed': true,
    'mode.tools': true,
    'mode.app-manage': true,
    'mode.admin-page': true,
    'mode.plugin': true,
    'mode.skill-create': true,

    /* Top-level page gates (Phase 4) — unchanged. */
    'page.pricing': true,
    'page.terms': true,
    'page.privacy': true,
    'page.contact-sales': true,
    'page.marketing-consent': true,
    'page.plugin': true,
    'page.admin': true,
    'page.app-manage': true,
    'page.skill-create': true,

    /* ================================================================
     * Phase 11 — sub-feature flags.
     *
     * Every flag below is `true` for B2C so no user-visible behaviour
     * changes during Phase 11-1. The gates themselves are inserted by
     * Phase 11-5 once the workspace UI has been hoisted into
     * `@auw/workspace-app`. Enterprise tenants (samsung, sungwoo, …)
     * will selectively set some to `false` in Phase 11-7.
     *
     * See docs/workspace-feature-map.md for the rationale per flag.
     * ================================================================ */

    /* mode.agent.* — composer menu / collab / egress controls. */
    'mode.agent.groupChat': true,
    'mode.agent.skillUpload': true,
    'mode.agent.skillBrowse': true,
    'mode.agent.cloudAttach': true,
    'mode.agent.mailAttach': true,
    'mode.agent.collabAttach': true,
    'mode.agent.messengerAttach': true,
    'mode.agent.liveView': true,
    'mode.agent.outputDownload': true,
    'mode.agent.reactions': true,

    /* mode.doc-agent.* */
    'mode.doc-agent.aiAssist': true,
    'mode.doc-agent.mediaEmbed': true,
    'mode.doc-agent.export': true,
    /* HWPX studio 풀 편집기(rhwp-studio 벤더링). 검증 전 기본 OFF →
       기존 RhwpHwpxEditorHost(보기+AI패치) 유지.
       ⚠️ 로컬 Phase 1~5 검증을 위해 임시 true. 배포/커밋 전 반드시 false 로 복귀. */
    'mode.doc-agent.hwpx-studio-editor': true,
    /* PR-FE-3 — chat-panel parity with unified agent. doc-agent's
     * 40% chat panel reuses ``ChatArea``'s MessageInput/MessageBubble
     * (PR-FE-2), so the ``+`` dropdown menus (cloud / mail / collab
     * messenger / skill upload-browse) and bubble-level affordances
     * (output download, reactions) need explicit enables here. The
     * defaults mirror ``mode.agent.*`` so b2c users see the same UX
     * regardless of which workspace they're in.
     *
     * ``liveView`` and ``groupChat`` stay off — the doc-agent panel
     * has no browser-live-view surface and is single-user, not a
     * group thread. */
    'mode.doc-agent.cloudAttach': true,
    'mode.doc-agent.mailAttach': true,
    'mode.doc-agent.collabAttach': true,
    'mode.doc-agent.messengerAttach': true,
    'mode.doc-agent.skillUpload': true,
    'mode.doc-agent.skillBrowse': true,
    'mode.doc-agent.outputDownload': true,
    'mode.doc-agent.reactions': true,
    'mode.doc-agent.liveView': false,
    'mode.doc-agent.groupChat': false,

    /* mode.message.* (DM) */
    'mode.message.groupThread': true,
    'mode.message.fileAttach': true,
    'mode.message.systemAppChannel': true,

    /* mode.storage.* */
    'mode.storage.teamFiles': true,
    'mode.storage.personalFiles': true,
    'mode.storage.dmFiles': true,
    'mode.storage.skillsNav': true,
    'mode.storage.multiSelect': true,

    /* mode.widget.* */
    'mode.widget.dock.agent': true,
    'mode.widget.dock.chat': true,
    'mode.widget.dock.calendar': true,
    'mode.widget.dock.mail': true,
    'mode.widget.dock.memo': true,
    'mode.widget.cloudSync': true,

    /* mode.feed.* */
    'mode.feed.autoRefresh': true,
    'mode.feed.externalLinks': true,

    /* mode.live-agent.* */
    'mode.live-agent.transcription': true,

    /* Settings tabs (Phase 11 — inside SettingsModal). */
    'page.settings.profile': true,
    'page.settings.subscription': true,
    'page.settings.billing': true,
    'page.settings.service': true,
    'page.settings.contact': true,

    /* settings.* — granular controls inside the tabs. */
    'settings.profile.photo': true,
    'settings.profile.deleteAccount': true,
    'settings.locale': true,
    'settings.personalization': true,
    'settings.appearance.wallpaper': true,
    'settings.consents.marketing': true,
    'settings.consents.cookies': true,

    /* billing.* — SaaS-only surfaces. */
    'billing.customerPortal': true,
    'billing.trialUpsell': true,
    'billing.downgradeFree': true,

    /* auth.* — landing / onboarding surrounding UI. */
    'auth.desktopDownload': false,
    'auth.marketingLanding': true,
    'auth.cookieBanner': true,
    'auth.onboarding': true,
    'auth.teamInvite': true,

    /* admin.* — team / app / skill console sub-features. */
    'admin.teams.create': true,
    'admin.teams.tokenPool': true,
    'admin.teams.inviteLinks': true,
    'admin.teams.memberTokenAllocation': true,
    'admin.app.roleChange': true,
    'admin.app.planChange': true,
    'admin.app.usageDashboard': true,
    'admin.app.recentActivity': true,
    'admin.skills.editor': true,
    'admin.skills.lifecycle': true,

    /* share.* — public-share surfaces. Enterprise-priority off flag. */
    'share.publicView': true,
    'share.passwordProtect': true,
    'share.signupCta': true,

    /* workspace.* — shell-level cross-cutting. */
    'workspace.collab.groupChat': true,
    'workspace.liveView': true,
    'workspace.sessionFiles': true,
    'workspace.wallpaper': true,
    'workspace.fileDrop': true,
    'workspace.aiDisclaimer': true,

    /* sidebar.* — B2C keeps its classic sidebar; the enterprise-only
     * `adminConsoleLink` is the single flag that is **false** here.
     * `contactLink` and `changePlanItem` stay on so the SaaS shell
     * keeps its contact button and the "변경하기" entry (the latter
     * deep-links into `/pricing`, which B2C exposes via
     * `page.pricing: true`). */
    'sidebar.favorites': true,
    'sidebar.teamSwitcher': true,
    'sidebar.managementSection': true,
    'sidebar.usageUpgrade': true,
    'sidebar.profileMenu': true,
    'sidebar.adminConsoleLink': false,
    'sidebar.contactLink': true,
    'sidebar.changePlanItem': true,

    /* page.admin.* — enterprise /admin/* routes. B2C does not ship the
     * enterprise admin app, so these are trivially false. */
    'page.admin.users': false,
    'page.admin.workspaces': false,
    'page.admin.usage': false,
  },

  /**
   * Workspace selection (Phase 4c).
   *
   * `modeOrder` mirrors `WORKSPACE_ORDER` from `@auw/app-shell` so the
   * B2C sidebar preserves its exact pre-Phase-4c ordering. On-prem
   * tenants can shorten, reorder, or extend this list without touching
   * app code.
   *
   * `defaultMode` records that the public product logically lands on
   * the "Agents" start page after sign-in. `defaultPath` is the literal
   * URL consumed by `AppRouter`, `AuthGuard`, `Layout`, and
   * `AuthCallbackPage` — centralised here so the post-login landing
   * destination lives in one place.
   */
  workspace: {
    modeOrder: ['agent', 'live-agent', 'doc-agent', 'document'],
    defaultMode: 'agent',
    defaultPath: '/agents',
  },

  /**
   * Plugin catalog (Phase 4b).
   *
   * B2C keeps exact parity with the pre-Phase-4b `PluginModal.tsx`
   * behaviour: Google Drive and OneDrive are hidden from the grid
   * (the backend still exposes them as MCP connectors, but the UI
   * does not surface them as standalone plugin cards). No other
   * rebrandings or reorderings — B2C uses the builtin descriptor
   * set as-is.
   *
   * Enterprise tenants can:
   *   - set `enabled: false` to hide the entire plugin page
   *   - add plugin ids to `hiddenIds` to suppress them
   *   - pass an `order` array to pin specific plugins to the top
   *   - rebrand any plugin via `overrides[id]` (label / logo tint /
   *     catalog copy / category grouping)
   */
  plugins: {
    hiddenIds: ['google_drive', 'onedrive', 'zoom'],
  },

  /**
   * Auth providers (Phase 5).
   *
   * B2C only ships Google OAuth; the entry here mirrors that. The
   * runtime adapter factory (`googleOAuthProvider`) is constructed in
   * `main.tsx` and supplied with the concrete `buildLoginUrl` /
   * `exchangeAuthCode` callbacks from `@auw/api-client`, keeping the
   * tenant manifest free of functions (and therefore serialisable).
   *
   * Enterprise tenants can append SAML / OIDC / email+password
   * entries here once their adapters replace the NotImplemented
   * stubs in `@auw/auth`.
   */
  auth: {
    providers: [
      {
        id: 'google',
        kind: 'google_oauth',
        labelKey: 'auth.login.continueWithGoogle',
      },
    ],
    defaultProviderId: 'google',
    /**
     * Legacy B2C invite flow: `/invite/team/:token` drops an already-
     * authed user on `/agents?teamInvite=...`. Phase 11-3a centralises
     * the literal here; on-prem tenants that do not ship team invites
     * can omit this field and `resolveTenantInviteRedirectPath()`
     * falls back to `workspace.defaultPath`.
     */
    inviteRedirectPath: '/agents',
  },

  /**
   * Role tree (Phase 6).
   *
   * B2C re-expresses the legacy owner/admin/user trinity as explicit
   * permission grants so the UI layer no longer pattern-matches role
   * strings directly. The resulting behaviour is 1:1 with pre-Phase-6:
   *
   *   - `owner` carries `'*'` — grants every admin permission.
   *     Matches the previous `userRole === 'owner'` gate that surfaced
   *     `/settings/apps` (app-manage) and `/settings/skills`
   *     (skill-create) in the sidebar.
   *   - `admin` receives only `ADMIN_FEED_REFRESH`. Matches the
   *     previous `role === 'owner' || role === 'admin'` gate on the
   *     manual feed refresh button.
   *   - `user` grants nothing. Matches the default denial path.
   *
   * `defaultRoleId: 'user'` pins the restrictive fallback for users
   * whose `/me` response omits a role or returns an unknown value —
   * again mirroring `resolveRole()` in `userStore`.
   *
   * Enterprise tenants can replace the entire tree without touching
   * shared code; new permission ids can be minted outside of
   * `@auw/permissions` and granted here directly.
   */
  roles: {
    defaultRoleId: 'user',
    definitions: [
      {
        id: 'owner',
        labelKey: 'roles.owner',
        policy: { grants: '*' },
      },
      {
        id: 'admin',
        labelKey: 'roles.admin',
        policy: { grants: [ADMIN_FEED_REFRESH] },
      },
      {
        id: 'user',
        labelKey: 'roles.user',
        policy: { grants: [] },
      },
    ],
  },

  /**
   * Analytics providers (Phase 8).
   *
   * B2C opts into both GA4 and Sentry. The concrete measurementId /
   * DSN are still read from `VITE_*` env vars in the loaders so
   * existing deployment secrets keep working — the tenant flag is
   * strictly additive (primary gate). On-prem tenants omit the whole
   * `analytics` block or set `enabled: false` to prevent any
   * telemetry network hit.
   */
  analytics: {
    ga4: { enabled: true },
    sentry: { enabled: true },
  },

  /**
   * Payment providers (Phase 8).
   *
   * B2C ships Paddle checkout for subscription upgrades. Enterprise
   * on-prem manifests MUST leave `payments` absent (or
   * `paddle.enabled: false`) per the Phase 4 agreement — billing is a
   * SaaS-only capability.
   */
  payments: {
    paddle: { enabled: true },
  },

  /**
   * 전문 에이전트 (specialized agents) — 사이드바의 별도 섹션으로
   * 노출된다. 각 테넌트는 자신의 매니페스트에 도메인 특화 에이전트를
   * 등록한다. 빈 배열이거나 필드 자체를 생략하면 사이드바 섹션이
   * 노출되지 않는다.
   *
   * B2C 는 더 이상 CAD / 견적 에이전트를 노출하지 않는다 (엔터프라이즈
   * 전용 기능). 두 에이전트는 각 엔터프라이즈 테넌트 매니페스트에서만
   * 등록한다.
   */
  specializedAgents: [],
});

/**
 * Export the permission ids used by B2C so consumers can reference
 * them directly without pulling in `@auw/permissions` everywhere —
 * keeps import graphs tidy and makes the "what does this code
 * actually check?" grep simpler.
 */
export {
  ADMIN_FEED_REFRESH,
  ADMIN_SKILLS_MANAGE,
  ADMIN_USERS_MANAGE,
  ADMIN_USERS_VIEW,
};
